import { CategoryBadge } from '../lib/categories';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, fcfa } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Star, Radio, ShoppingBag, Users, Camera, Plus, Pencil, MapPin, Clock, Truck } from 'lucide-react';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { FollowButton } from '../components/FollowButton';
import { imgSrc } from '../lib/cart';
import { ShareShop } from '../components/ShareShop';
import { MapView } from '../components/MapView';

export default function SellerPublic() {
  const { sellerId } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ rating: 5, comment: '' });
  const [photo, setPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Image trop lourde (5 Mo max)'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/uploads/review-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhoto(data.path); toast.success('Photo ajoutée');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Échec de l\'envoi'); }
    finally { setUploading(false); }
  };
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/sellers/${sellerId}`); setData(data); } catch {}
  };
  useEffect(() => { load(); }, [sellerId]);

  const submitReview = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/sellers/${sellerId}/reviews`, { ...form, photo_path: photo }); toast.success('Merci !'); setForm({ rating: 5, comment: '' }); setPhoto(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">Loading…</div></div>;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="card-elev rounded-3xl overflow-hidden">
          {data.shop_banner && <img src={imgSrc(data.shop_banner)} alt="" data-testid="shop-banner-img" className="w-full h-40 sm:h-56 object-cover" />}
          <div className="p-6 flex items-center gap-5 flex-wrap">
          {data.shop_logo ? (
            <img src={imgSrc(data.shop_logo)} alt="" data-testid="shop-logo-img" className={`h-20 w-20 rounded-2xl object-cover border-2 border-slate-900 ${data.shop_banner ? '-mt-14' : ''}`} />
          ) : (
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-600 to-sky-400 grid place-items-center text-3xl font-display font-extrabold text-white">
              {data.name?.[0] || 'S'}
            </div>
          )}
          <div className="flex-1">
            <h1 className="font-display text-3xl font-extrabold flex items-center gap-2 flex-wrap" data-testid="seller-name">{data.shop_name || data.name}<VerifiedBadge verified={data.verified} size="lg" testId="verified-seller-public" />
              <FollowButton sellerId={sellerId} following={data.is_following} followersCount={data.followers_count} onChange={(d) => setData({ ...data, is_following: d.following, followers_count: d.followers_count })} />
            </h1>
            <div className="mt-1 flex items-center gap-4 text-sm text-slate-300 flex-wrap">
              {data.shop_city && <span className="inline-flex items-center gap-1 text-slate-400"><MapPin className="h-4 w-4" /> {data.shop_city}</span>}
              <span className="inline-flex items-center gap-1">
                <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                <span data-testid="seller-rating" className="font-mono-num">{data.avg_rating ?? '—'}</span>
                <span className="text-slate-500">({data.reviews.length})</span>
              </span>
              <span className="inline-flex items-center gap-1 text-slate-400">
                <ShoppingBag className="h-4 w-4" /> {data.products_sold} vendus
              </span>
              <span className="inline-flex items-center gap-1 text-slate-400">
                <Radio className="h-4 w-4" /> {data.active_lives.length} en direct
              </span>
            </div>
            {data.shop_description && <p className="mt-2 text-sm text-slate-400" data-testid="shop-description">{data.shop_description}</p>}
            {(data.shop_hours || data.shop_delivery) && (
              <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm" data-testid="shop-info">
                {data.shop_hours && <div className="flex gap-2 rounded-xl bg-slate-900/60 border border-slate-800 p-2.5" data-testid="shop-hours"><Clock className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" /><div><div className="text-[10px] uppercase tracking-widest text-slate-500">Horaires</div><div className="text-slate-200">{data.shop_hours}</div></div></div>}
                {data.shop_delivery && <div className="flex gap-2 rounded-xl bg-slate-900/60 border border-slate-800 p-2.5" data-testid="shop-delivery"><Truck className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /><div><div className="text-[10px] uppercase tracking-widest text-slate-500">Livraison</div><div className="text-slate-200">{data.shop_delivery}</div>{data.shop_delivery_fee > 0 ? <div className="text-xs text-amber-300 mt-0.5">{fcfa(data.shop_delivery_fee)} par commande{data.shop_free_delivery_from > 0 ? ` · offerte dès ${fcfa(data.shop_free_delivery_from)}` : ''}</div> : <div className="text-xs text-emerald-300 mt-0.5">Livraison offerte</div>}</div></div>}
              </div>
            )}
            <div className="mt-3"><ShareShop sellerId={sellerId} shopName={data.shop_name || data.name} owner={user?.user_id === data.user_id} /></div>
            {data.shop_lat != null && <div className="mt-3"><MapView markers={[{ key: 's', pos: [data.shop_lat, data.shop_lng], icon: 'shop', label: data.shop_name || data.name }]} height="h-40" zoom={15} testid="public-shop-map" /></div>}
          </div>
          </div>
        </div>

        {/* Active lives */}
        {data.active_lives.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
              <span className="live-dot" />{t('live_now')}
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {data.active_lives.map((l) => (
                <Link key={l.live_id} to={`/live/${l.live_id}`} data-testid={`active-live-${l.live_id}`}
                  className="card-elev rounded-2xl overflow-hidden hover:border-rose-500/50 transition">
                  <div className="aspect-video overflow-hidden bg-black">
                    {l.thumbnail && <img src={l.thumbnail} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-3">
                    <div className="font-bold truncate">{l.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {l.viewers}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Owner actions */}
        {user && user.user_id === data.user_id && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl border border-rose-500/40 bg-rose-500/10" data-testid="owner-bar">
            <div className="text-sm text-rose-100">C'est votre boutique. {data.products?.length ? `${data.products.length} produit(s) en ligne.` : 'Aucun produit pour le moment.'}</div>
            <div className="flex gap-2">
              <Link to="/seller?tab=articles&add=1" data-testid="owner-add-product" className="inline-flex items-center gap-1.5 text-sm font-bold text-white btn-brand rounded-full px-4 py-2"><Plus className="h-4 w-4" />Ajouter un produit</Link>
              <Link to="/seller?tab=articles" data-testid="owner-edit-shop" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-100 rounded-full border border-slate-600 px-4 py-2"><Pencil className="h-4 w-4" />Modifier la boutique</Link>
            </div>
          </div>
        )}

        {/* Products catalogue */}
        {data.products?.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-bold mb-3">Articles ({data.products.length})</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="seller-products">
              {data.products.map((p) => (
                <Link to={`/product/${p.product_id}`} key={p.product_id} data-testid={`seller-prod-${p.product_id}`} className="rounded-xl bg-slate-900/60 border border-slate-800 overflow-hidden hover:border-sky-500/50">
                  {p.image_url && <img src={imgSrc(p.image_url)} alt="" className="h-36 w-full object-cover" />}
                  <div className="p-3">
                    <div className="font-bold truncate">{p.name}</div>
                    <CategoryBadge category={p.category} size={p.size} brand={p.brand} className="mt-1" />
                    <div className="mt-1 text-amber-400 font-mono-num text-sm">{p.price > 0 ? fcfa(p.price) : `Enchère · dès ${fcfa(p.starting_price)}`}</div>
                    {p.price > 0 && !(p.stock > 0) && <div className="text-xs text-rose-400 mt-1">Rupture</div>}
                    {p.sold && <div className="text-xs text-emerald-400 mt-1">Vendu</div>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Past lives */}
        {data.past_lives.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-bold mb-3">Lives passés</h2>
            <div className="grid md:grid-cols-4 gap-3">
              {data.past_lives.map((l) => (
                <div key={l.live_id} className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 text-sm">
                  <div className="font-bold truncate">{l.title}</div>
                  <div className="text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        <section className="card-elev rounded-2xl p-5">
          <h2 className="font-display text-xl font-bold mb-4">Avis ({data.reviews.length})</h2>
          <div className="space-y-3">
            {data.reviews.length === 0 && <div className="text-slate-500 text-sm">Pas encore d'avis.</div>}
            {data.reviews.map((r) => (
              <div key={r.review_id} data-testid={`review-${r.review_id}`} className="p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-bold text-amber-400">{r.buyer_name}</span>
                  <span className="inline-flex">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-700'}`} />
                    ))}
                  </span>
                  <span className="text-slate-500 text-xs ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                {r.comment && <div className="mt-1 text-slate-200 text-sm">{r.comment}</div>}
                {r.photo_path && <img data-testid={`review-photo-${r.review_id}`} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_path}`} alt="Photo de l'article reçu"
                  className="mt-2 h-32 w-32 object-cover rounded-xl border border-white/10" loading="lazy" />}
              </div>
            ))}
          </div>

          {/* Post review form: only for buyer with delivered order */}
          {user && user.user_id !== data.user_id && (
            <form onSubmit={submitReview} className="mt-6 space-y-3 border-t border-slate-800 pt-5">
              <div className="text-sm text-slate-300">Noter ce vendeur (si vous avez reçu une commande)</div>
              <div className="flex gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} type="button" data-testid={`star-${n}`}
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1">
                    <Star className={`h-6 w-6 ${n <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                  </button>
                ))}
              </div>
              <textarea data-testid="review-comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                rows={3} placeholder="Votre avis…"
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <div className="flex flex-wrap items-center gap-3">
                <label data-testid="review-photo-label" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-700 text-slate-200 text-sm cursor-pointer hover:border-sky-400/60">
                  <Camera className="h-4 w-4" />{uploading ? 'Envoi…' : (photo ? 'Changer la photo' : 'Ajouter une photo de l\'article reçu')}
                  <input data-testid="review-photo-input" type="file" accept="image/*,.heic,.heif" className="hidden" onChange={uploadPhoto} disabled={uploading} />
                </label>
                {photo && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} alt="" className="h-14 w-14 object-cover rounded-lg border border-white/10" data-testid="review-photo-preview" />}
              </div>
              <Button disabled={busy} type="submit" data-testid="submit-review"
                className="btn-brand text-white">Publier l'avis</Button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
